import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  School as SchoolIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import WeChatQRDialog from '../common/WeChatQRDialog';
import useWeChatQR from '../../hooks/useWeChatQR';

const HeroSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const { openQR, openWeChatQR, closeWeChatQR } = useWeChatQR();

  const locations = ['吉隆坡', '雪兰莪', '槟城', '柔佛', '沙巴', '马六甲', '森美兰', '霹雳', '吉打', '玻璃市', '登嘉楼', '吉兰丹', '彭亨', '纳闽', '砂拉越'];

  const universityTypes = ['全部大学','公立大学', '私立大学'];

  // 处理搜索
  const handleSearch = () => {
    const params = new URLSearchParams();
    
    if (searchQuery.trim()) {
      params.append('search', searchQuery.trim());
    }
    if (selectedLocation) {
      params.append('location', selectedLocation);
    }
    if (selectedType && selectedType !== '全部大学') {
      params.append('type', selectedType);
    }
    
    const queryString = params.toString();
    const url = queryString ? `/universities?${queryString}` : '/universities';
    navigate(url);
  };

  // 处理热门标签点击
  const handleTagClick = (tag) => {
    const params = new URLSearchParams();
    params.append('search', tag);
    navigate(`/universities?${params.toString()}`);
  };

  // 处理回车键搜索
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const quickStats = [
    { number: '30+', label: '合作院校', icon: <SchoolIcon /> },
    { number: '5000+', label: '成功案例', icon: <TrendingIcon /> },
    { number: '15+', label: '专业领域', icon: <LanguageIcon /> },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.1)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          animation: 'float 6s ease-in-out infinite',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <Container maxWidth="xl" sx={{ zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={4} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} lg={6}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  开启您的
                  <br />
                  马来西亚留学之旅
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    lineHeight: 1.6,
                  }}
                >
                  专业的留学咨询服务，助您找到最适合的马来西亚大学和专业。
                  从申请到入学，我们为您提供全程指导和支持。
                </Typography>
              </motion.div>

              {/* Search Form */}
              <motion.div variants={itemVariants}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 4,
                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                >
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  快速搜索理想院校
                </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        placeholder="搜索大学或专业..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                        variant="outlined"
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        select
                        value={selectedLocation}
                        onChange={(e) => setSelectedLocation(e.target.value)}
                        variant="outlined"
                        size="medium"
                        placeholder="选择地区"
                      >
                        <MenuItem value="">全部地区</MenuItem>
                        {locations.map((location) => (
                          <MenuItem key={location} value={location}>
                            {location}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        variant="outlined"
                        size="medium"
                        placeholder="选择类型"
                      >
                        <MenuItem value="">全部类型</MenuItem>
                        {universityTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        sx={{ height: '100%', borderRadius: 2 }}
                      >
                        搜索
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div variants={itemVariants}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={openWeChatQR}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 25,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                    }}
                  >
                    免费咨询评估
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/universities')}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 25,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                    }}
                  >
                    查看院校排名
                  </Button>
                </Box>
              </motion.div>

              {/* Popular Search Tags */}
              <motion.div variants={itemVariants}>
                <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                  热门搜索：
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['马来亚', '国民', '博特拉', '诺丁汉', '泰莱', '理科', '思特雅', '理工', 'USM', 'UTM', 'UPM', 'UKM', 'Taylor'].map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      variant="outlined"
                      size="small"
                      clickable
                      onClick={() => handleTagClick(tag)}
                      sx={{
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    />
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Right Content - Stats */}
            <Grid item xs={12} lg={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {quickStats.map((stat, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    custom={index}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        background: alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                        },
                      }}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 0 }}>
                        <Box
                          sx={{
                            mr: 3,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                              mb: 0.5,
                            }}
                          >
                            {stat.number}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>

      <WeChatQRDialog 
        open={openQR} 
        onClose={closeWeChatQR}
        title="免费咨询评估"
      />
    </Box>
  );
};

export default HeroSection;
